import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Caption1, Input, Spinner, Text, Tooltip, tokens } from '@fluentui/react-components';
import {
  CheckmarkRegular,
  DocumentRegular,
  HistoryRegular,
  PeopleRegular,
  PersonRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import { useApi } from '../../api/useApi';
import { useAsync } from '../../api/useAsync';
import type { TeamSummaryDto } from '../../api/types';
import '../groups/groups.css';
import './teams.css';

type SortKey = 'name' | 'memberCount' | 'guestCount' | 'fileCount' | 'lastActivity';

interface Props {
  selected: Set<string>;
  onToggle: (team: TeamSummaryDto) => void;
}

/** Linke Auswahlliste der Teams mit Spalten Mitglieder / Dateien / Letzte Aktivität, sortierbar. */
export function TeamList({ selected, onToggle }: Props) {
  const api = useApi();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const timer = useRef<number | undefined>(undefined);

  function onSearch(value: string) {
    setSearch(value);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setQuery(value.trim()), 300);
  }

  const teams = useAsync(() => api.listTeams(query || undefined), [api, query]);
  const items = teams.data ?? [];

  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  function toggleSort(key: SortKey) {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }

  const sorted = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      let cmp: number;
      if (sortKey === 'name') cmp = a.displayName.localeCompare(b.displayName, 'de', { sensitivity: 'base' });
      else if (sortKey === 'lastActivity') cmp = (a.lastActivity ?? '').localeCompare(b.lastActivity ?? '');
      else cmp = (a[sortKey] ?? -1) - (b[sortKey] ?? -1);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [items, sortKey, sortDir]);

  const ind = sortDir === 'asc' ? '▲' : '▼';
  const sortBtn = (key: SortKey, label: string, icon: ReactNode) => (
    <Tooltip relationship="label" content={`${label} — sortieren`}>
      <button
        className={sortKey === key ? 'grp-sort grp-sort-col active' : 'grp-sort grp-sort-col'}
        onClick={() => toggleSort(key)}
      >
        {icon}
        {sortKey === key ? <span className="grp-sort-ind">{ind}</span> : null}
      </button>
    </Tooltip>
  );

  return (
    <div className="grp-panel grp-list-panel">
      <div className="grp-panel-head">
        <span className="grp-panel-title">Teams</span>
        {items.length ? (
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            {selected.size}/{items.length} gewählt
          </Caption1>
        ) : null}
      </div>

      <Input
        contentBefore={<SearchRegular />}
        placeholder="Team suchen…"
        value={search}
        onChange={(_, d) => onSearch(d.value)}
      />

      {teams.loading ? (
        <div className="grp-empty">
          <Spinner size="tiny" label="Lade Teams…" />
        </div>
      ) : teams.error ? (
        <div className="grp-empty">
          <Text>{teams.error.message}</Text>
        </div>
      ) : items.length === 0 ? (
        <div className="grp-empty">Keine Teams gefunden.</div>
      ) : (
        <>
          <div className="tm-list-head">
            <span />
            <button className={sortKey === 'name' ? 'grp-sort active' : 'grp-sort'} onClick={() => toggleSort('name')}>
              Team
              {sortKey === 'name' ? <span className="grp-sort-ind">{ind}</span> : null}
            </button>
            {sortBtn('memberCount', 'Mitglieder', <PeopleRegular />)}
            {sortBtn('guestCount', 'Gäste', <PersonRegular />)}
            {sortBtn('fileCount', 'Dateien', <DocumentRegular />)}
            {sortBtn('lastActivity', 'Letzte Aktivität', <HistoryRegular />)}
          </div>

          <div className="grp-list">
            {sorted.map((t) => {
              const isSelected = selected.has(t.id);
              return (
                <div
                  key={t.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  className={isSelected ? 'tm-row selected' : 'tm-row'}
                  onClick={() => onToggle(t)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onToggle(t);
                    }
                  }}
                >
                  <span className="grp-row-check">{isSelected ? <CheckmarkRegular /> : null}</span>
                  <span className="grp-row-main">
                    <span className="grp-row-name">{t.displayName}</span>
                    {t.description ? <span className="grp-row-sub">{t.description}</span> : null}
                  </span>
                  <span className="grp-cell">
                    <PeopleRegular />
                    {t.memberCount}
                  </span>
                  <span className="grp-cell" title="Gäste">
                    <PersonRegular />
                    {t.guestCount}
                  </span>
                  <span className="grp-cell">
                    <DocumentRegular />
                    {t.fileCount ?? '—'}
                  </span>
                  <span className="tm-cell-date">{t.lastActivity ?? '—'}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
